<template>
  <Teleport to="body">
    <div class="exclusion-list-backdrop fixed inset-0 z-[3000] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
      @click.self="emit('close')">
      <section
        class="exclusion-list-panel flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white text-slate-950 shadow-2xl"
        role="dialog" aria-modal="true" aria-labelledby="exclusion-list-title">

        <!-- Top Progress Bar (indeterminate) -->
        <div class="exclusion-progress-bar" v-if="store.loading || store.saving || togglingGroupId">
          <div class="exclusion-progress-bar-inner"></div>
        </div>

        <div class="exclusion-list-header flex items-center justify-between px-6 pt-6">
          <h2 id="exclusion-list-title" class="m-0 text-xl font-extrabold tracking-normal text-slate-950">
            Exclusion Groups
          </h2>
          <button type="button"
            class="flex h-9 w-9 items-center justify-center rounded-md bg-transparent p-0 text-slate-700 hover:bg-slate-100 hover:text-violet-700"
            aria-label="Close" @click="emit('close')" :disabled="store.saving">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="content-exclusion-form flex-1 overflow-y-auto px-6 pb-0"
          :class="{ 'exclusion-busy': store.saving }">
          <div class="mt-6 flex w-full max-w-md rounded-md bg-slate-100 p-1">
            <button type="button"
              class="h-10 flex-1 rounded-md font-bold text-sm transition-all duration-200"
              :class="activeTab === 'groups' ? 'bg-white text-violet-700 shadow-sm' : 'bg-transparent text-slate-600 hover:text-slate-900'"
              @click="switchTab('groups')">
              Exclusion Groups
            </button>
            <button type="button"
              class="flex h-10 flex-1 items-center justify-center gap-2 rounded-md font-bold text-sm transition-all duration-200"
              :class="activeTab === 'symbols' ? 'bg-white text-violet-700 shadow-sm' : 'bg-transparent text-slate-600 hover:text-slate-900'"
              @click="switchTab('symbols')">
              Excluded Symbols
              <span class="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700">
                {{ store.excludedSymbolCount }}
              </span>
            </button>
          </div>

          <!-- Tab 1: Exclusion Groups -->
          <div v-if="activeTab === 'groups'">
            <div class="mt-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <p class="m-0 text-sm text-slate-700">
                Choose symbol groups to exclude before applying the main filter.
              </p>
              <button type="button"
                class="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-5 font-extrabold text-violet-800 hover:bg-violet-700 hover:text-white"
                @click="openCreateForm" :disabled="store.saving">
                <i class="fa-solid fa-plus"></i>
                Add Group
              </button>
            </div>

            <!-- Loading state: initial fetch -->
            <div v-if="store.loading && !hasGroups" class="py-10 text-center text-slate-500">
              <div class="exclusion-loading-block">
                <span class="exclusion-spinner"></span>
                <span>Loading exclusion groups...</span>
              </div>
            </div>

            <template v-else>
              <ExclusionGroupTable title="Default Groups" :groups="store.systemGroups" readonly
                :toggling-id="togglingGroupId"
                @view="openGroupDetail" @toggle="toggleCustomGroup"/>

              <ExclusionGroupTable title="Custom Groups" :groups="customGroupsFiltered"
                :toggling-id="togglingGroupId"
                @view="openGroupDetail" @toggle="toggleCustomGroup" />
            </template>
          </div>

          <!-- Tab 2: Excluded Symbols List (Categorized and listed top-to-bottom) -->
          <div v-else class="exclusion-symbols-tab mt-5">
            <div class="exclusion-symbol-search-row">
              <div class="exclusion-symbol-search-wrapper">
                <input v-model="symbolSearchQuery" type="text" placeholder="Search symbols..."
                  class="exclusion-symbol-search-input" />
                <i class="fa-solid fa-magnifying-glass exclusion-symbol-search-icon"></i>
              </div>
              <div class="exclusion-symbol-count-label">
                Showing {{ totalFilteredSymbolsCount }} / {{ store.excludedSymbolCount }} excluded symbols
              </div>
            </div>

            <!-- Loading state: fetching symbols -->
            <div v-if="loadingSymbols" class="py-16 text-center text-slate-500">
              <div class="exclusion-loading-block">
                <span class="exclusion-spinner"></span>
                <span>Loading excluded symbols list...</span>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="excludedSymbolsByGroup.length === 0" class="py-16 text-center text-slate-400 text-sm border border-dashed border-slate-300 rounded-md">
              <i class="fa-solid fa-folder-open text-2xl mb-2 block text-slate-300"></i>
              No symbols excluded.
            </div>

            <!-- List by Category Group -->
            <div v-else class="exclusion-symbols-container">
              <div v-for="group in excludedSymbolsByGroup" :key="group.id" class="exclusion-group-block">
                <h3 class="exclusion-group-block-title">
                  <i class="fa-solid fa-layer-group text-violet-600"></i>
                  <span>{{ group.name }}</span>
                  <span class="exclusion-group-badge-count">{{ group.symbols.length }}</span>
                </h3>
                
                <div class="exclusion-group-symbols-grid">
                  <div v-for="item in group.symbols" :key="item.symbol" 
                    class="exclusion-symbol-item" :data-tooltip="item.coinName">
                    <span class="exclusion-symbol-badge">
                      {{ item.symbol }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer
          class="flex flex-col gap-4 border-t border-slate-200 bg-white px-6 py-5 md:flex-row md:items-center md:justify-between">
          <div class="text-sm text-slate-600">
            <i class="fa-solid fa-circle-info mr-2 text-violet-700"></i>
            Total symbols excluded:
            <strong class="text-violet-700">{{ store.excludedSymbolCount.toLocaleString() }}</strong>
          </div>
          <div class="flex gap-3">
            <button type="button"
              class="h-11 min-w-24 rounded-md border border-slate-300 bg-white px-5 font-extrabold text-slate-950 hover:bg-slate-50"
              @click="emit('close')" :disabled="store.saving">
              Cancel
            </button>
            <button type="button"
              class="h-11 min-w-36 rounded-md bg-violet-700 px-5 font-extrabold text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="store.saving" @click="saveAndClose">
              <span v-if="store.saving" class="exclusion-btn-loading">
                <span class="exclusion-spinner exclusion-spinner-sm"></span>
                Saving...
              </span>
              <span v-else>Save</span>
            </button>
          </div>
        </footer>

        <ExclusionCustomForm v-if="isFormOpen" :group="selectedGroup" :available-symbols="availableSymbols"
          :blocked-symbols="currentBlockedSymbols" @close="closeForm" @saved="handleFormSaved" />
      </section>
    </div>
  </Teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useExclusionGroupsStore } from '@/stores/exclusionGroups';
import ExclusionGroupTable from './ExclusionGroupTable.vue';
import ExclusionCustomForm from './ExclusionCustomForm.vue';

defineProps({
  availableSymbols: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['apply', 'close']);
const store = useExclusionGroupsStore();
const { proxy } = getCurrentInstance();
const isFormOpen = ref(false);
const selectedGroup = ref(null);
const blockedSymbolsByGroup = ref({});
const togglingGroupId = ref(null);

const activeTab = ref('groups');
const groupSymbolsMap = ref({});
const loadingSymbols = ref(false);
const symbolSearchQuery = ref('');

function formatGroupName(name) {
  if (!name) return '-';
  const clean = String(name).trim().toUpperCase();
  if (clean === 'BSTOCKS' || clean === 'TRADFI_BSTOCKS') return 'TradFi_bStocks';
  if (clean === 'STOCK' || clean === 'TRADFI_FUTURES') return 'TradFi_Futures';
  if (clean === 'TCOMMODITIES' || clean === 'TRADFI_TCOMMODITIES') return 'TradFi_tCommodities';
  return name;
}

const excludedSymbolsByGroup = computed(() => {
  const query = symbolSearchQuery.value.trim().toUpperCase();
  const result = [];

  const activeGroups = [
    ...store.systemGroups,
    ...store.customGroups,
    ...store.blockedGroups
  ].filter(g => g.isExcluded);

  for (const g of activeGroups) {
    const rawSymbols = groupSymbolsMap.value[g.id] || [];
    const filtered = rawSymbols.map(s => ({
      symbol: String(s.symbol || s).trim().toUpperCase(),
      coinName: s.coinName || s.symbol || s
    })).filter(s => 
      !query || 
      s.symbol.includes(query) || 
      String(s.coinName).toUpperCase().includes(query)
    ).sort((a, b) => a.symbol.localeCompare(b.symbol));

    if (filtered.length > 0) {
      result.push({
        id: g.id,
        name: formatGroupName(g.name),
        symbols: filtered
      });
    }
  }
  return result;
});

const totalFilteredSymbolsCount = computed(() => {
  return excludedSymbolsByGroup.value.reduce((acc, group) => acc + group.symbols.length, 0);
});

async function loadAllExcludedSymbols() {
  const activeGroups = [
    ...store.systemGroups,
    ...store.customGroups,
    ...store.blockedGroups
  ].filter(g => g.isExcluded);

  const missingGroups = activeGroups.filter(g => !groupSymbolsMap.value[g.id]);
  if (missingGroups.length === 0) return;

  loadingSymbols.value = true;
  try {
    const details = await Promise.all(missingGroups.map(g => store.fetchGroupDetail(g.id)));
    missingGroups.forEach((g, index) => {
      groupSymbolsMap.value[g.id] = details[index]?.symbols || [];
    });
  } catch (error) {
    console.error("Error loading symbols for tab:", error);
  } finally {
    loadingSymbols.value = false;
  }
}

async function switchTab(tab) {
  activeTab.value = tab;
  if (tab === 'symbols') {
    await loadAllExcludedSymbols();
  }
}

const hasGroups = computed(() => (
  store.systemGroups.length > 0 ||
  store.blockedGroups.length > 0 ||
  store.customGroups.length > 0
));

const currentBlockedSymbols = computed(() => {
  const symbols = new Set();
  const currentGroupId = selectedGroup.value?.id;
  Object.entries(blockedSymbolsByGroup.value).forEach(([groupId, groupSymbols]) => {
    if (Number(groupId) === currentGroupId) return;
    groupSymbols.forEach(symbol => symbols.add(symbol));
  });
  return Array.from(symbols);
});

const customGroupsFiltered = computed(() => {
  return store.customGroups.filter(g => !isBlockGroup(g));
});

onMounted(async () => {
  if (!hasGroups.value) {
    await store.fetchGroups();
  }
});

async function openCreateForm() {
  await loadBlockedSymbols();
  selectedGroup.value = null;
  isFormOpen.value = true;
}

async function openGroupDetail(group) {
  const detail = await store.fetchGroupDetail(group.id);
  if (isBlockGroup(group)) {
    setBlockedSymbols(group.id, detail.symbols);
  } else {
    await loadBlockedSymbols();
  }
  selectedGroup.value = {
    ...detail,
    isSystem: group.isSystem,
    groupType: detail.groupType || group.groupType,
  };
  isFormOpen.value = true;
}

async function toggleCustomGroup(group) {
  togglingGroupId.value = group.id;
  try {
    await store.updateGroup(group.id, {
      isExcluded: !group.isExcluded,
    });
    notify('success', 'Exclusion group updated.');
  } catch (error) {
    console.error('Error updating exclusion group:', error);
    const errMsg = error?.response?.data?.message || error?.message || 'Unable to update exclusion group.';
    notify('error', errMsg);
  } finally {
    togglingGroupId.value = null;
  }
}

function closeForm() {
  isFormOpen.value = false;
  selectedGroup.value = null;
}

function handleFormSaved() {
  if (selectedGroup.value) {
    delete groupSymbolsMap.value[selectedGroup.value.id];
    if (activeTab.value === 'symbols') {
      loadAllExcludedSymbols();
    }
  }
  if (selectedGroup.value && isBlockGroup(selectedGroup.value)) {
    const nextSymbols = { ...blockedSymbolsByGroup.value };
    delete nextSymbols[selectedGroup.value.id];
    blockedSymbolsByGroup.value = nextSymbols;
  }
}

function saveAndClose() {
  emit('apply');
  emit('close');
}

function notify(type, message) {
  proxy?.$toast?.open({
    message,
    type,
    position: 'top-right',
  });
}

async function loadBlockedSymbols() {
  const missingGroups = store.blockedGroups.filter(group => !blockedSymbolsByGroup.value[group.id]);
  if (missingGroups.length === 0) return;

  const details = await Promise.all(missingGroups.map(group => store.fetchGroupDetail(group.id)));
  missingGroups.forEach((group, index) => {
    setBlockedSymbols(group.id, details[index]?.symbols);
  });
}

function setBlockedSymbols(groupId, symbols = []) {
  blockedSymbolsByGroup.value = {
    ...blockedSymbolsByGroup.value,
    [groupId]: symbols
      .map(item => String(item.symbol || item).trim().toUpperCase())
      .filter(Boolean),
  };
}

function isBlockGroup(group) {
  return group?.groupType === 'BLOCKED' || group?.groupType === 'BLOCK_CUSTOM' || group?.name === 'BLOCKED';
}

</script>

<style>
/* ── Progress Bar (indeterminate) ── */
.exclusion-progress-bar {
  position: relative;
  width: 100%;
  height: 3px;
  background: transparent;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 12px 12px 0 0;
}

.exclusion-progress-bar-inner {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, transparent, #F0B90B, transparent);
  border-radius: 2px;
  animation: exclusion-progress-slide 1.2s ease-in-out infinite;
}

@keyframes exclusion-progress-slide {
  0% { left: -40%; }
  100% { left: 100%; }
}

/* ── Busy Overlay ── */
.exclusion-busy {
  pointer-events: none;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

/* ── Spinner ── */
.exclusion-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(240, 185, 11, 0.2);
  border-top-color: #F0B90B;
  border-radius: 50%;
  animation: exclusion-spin 0.7s linear infinite;
  flex-shrink: 0;
}

.exclusion-spinner-sm {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes exclusion-spin {
  to { transform: rotate(360deg); }
}

/* ── Loading Block ── */
.exclusion-loading-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #5E6673;
  font-size: 13px;
}

/* ── Button Loading State ── */
.exclusion-btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* ── Toggle Checkbox Loading ── */
.exclusion-toggle-loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.exclusion-toggle-loading .exclusion-spinner {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

/* ── Dark Mode (legacy) ── */
body.dark-mode .exclusion-list-backdrop {
  background: rgba(15, 23, 42, 0.78);
}

body.dark-mode .exclusion-list-panel {
  background: #000000;
  border: 1px solid #363c4e;
  color: #ffffff;
}

body.dark-mode .exclusion-list-panel footer {
  background: #000000;
  border-color: #363c4e;
}

body.dark-mode .exclusion-list-panel h2,
body.dark-mode .exclusion-list-panel p,
body.dark-mode .exclusion-list-panel strong,
body.dark-mode .exclusion-list-panel .text-slate-950 {
  color: #ffffff;
}

body.dark-mode .exclusion-list-panel .text-slate-500,
body.dark-mode .exclusion-list-panel .text-slate-600,
body.dark-mode .exclusion-list-panel .text-slate-700 {
  color: #cbd5e1;
}

body.dark-mode .exclusion-list-panel button.bg-white {
  background: #2b3139;
  border-color: #475569;
  color: #eaecef;
}

body.dark-mode .exclusion-list-panel button.bg-white:hover {
  background: #363c4e;
}

body.dark-mode .exclusion-list-panel button.bg-violet-700 {
  background: #6d28d9;
  border-color: #6d28d9;
  color: #ffffff;
}

body.dark-mode .exclusion-list-panel button.bg-violet-700:hover {
  background: #5b21b6;
}

body.dark-mode .exclusion-list-panel .bg-slate-100 {
  background: #111827;
}

body.dark-mode .exclusion-list-panel .bg-violet-50 {
  background: rgba(109, 40, 217, 0.22);
}

body.dark-mode .exclusion-list-panel .bg-slate-300 {
  background: #475569;
}

body.dark-mode .content-exclusion-form,
body.dark-mode .exclusion-list-header {
  background: #09090b;
}

/* ── Excluded Symbols Tab Styling (Categorized) ── */
.exclusion-symbols-tab {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 1.5rem;
}

.exclusion-symbol-search-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 768px) {
  .exclusion-symbol-search-row {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.exclusion-symbol-search-wrapper {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.exclusion-symbol-search-input {
  height: 40px;
  width: 100%;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  padding: 0 40px 0 12px;
  font-size: 14px;
  color: #0f172a !important;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.exclusion-symbol-search-input:focus {
  border-color: #6d28d9;
  box-shadow: 0 0 0 3px rgba(109, 40, 217, 0.1);
}

.exclusion-symbol-search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.exclusion-symbol-count-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.exclusion-symbols-container {
  margin-top: 1rem;
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 1.25rem;
  background-color: #f8fafc;
}

/* Group Block */
.exclusion-group-block {
  margin-bottom: 2rem;
}

.exclusion-group-block:last-child {
  margin-bottom: 0;
}

.exclusion-group-block-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  font-weight: 800;
  color: #1e293b !important;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.exclusion-group-badge-count {
  background-color: #e2e8f0;
  color: #475569;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
}

.exclusion-group-symbols-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 0.75rem;
}

@media (min-width: 640px) {
  .exclusion-group-symbols-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1024px) {
  .exclusion-group-symbols-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

/* Symbol Row Item */
.exclusion-symbol-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  cursor: pointer;
}

.exclusion-symbol-item:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
  z-index: 50; /* Đảm bảo item đang hover hiển thị tooltip đè lên trên tất cả các item xung quanh */
}

/* Custom Tooltip text container */
.exclusion-symbol-item::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 125%; /* Hiển thị phía trên symbol */
  left: 50%;
  transform: translateX(-50%) translateY(6px);
  background-color: #1e293b; /* Màu tối phiến đá đậm sang trọng */
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.15), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.1s ease, transform 0.1s ease, visibility 0.1s ease;
  z-index: 9999;
}

/* Custom Tooltip arrow */
.exclusion-symbol-item::before {
  content: "";
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%) translateY(12px);
  border-width: 5px;
  border-style: solid;
  border-color: #1e293b transparent transparent transparent; /* Mũi tên trỏ xuống dưới */
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.1s ease, transform 0.1s ease, visibility 0.1s ease;
  z-index: 9999;
}

/* Hover state to trigger tooltip instantly */
.exclusion-symbol-item:hover::after {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.exclusion-symbol-item:hover::before {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(6px);
}

.exclusion-symbol-badge {
  display: inline-block;
  width: 100%;
  text-align: center;
  padding: 0.25rem 0.5rem;
  background-color: rgba(109, 40, 217, 0.08) !important;
  color: #6d28d9 !important;
  border: 1px solid rgba(109, 40, 217, 0.15);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 800;
}

/* Dark Mode Support for new elements */
body.dark-mode .exclusion-symbol-search-input {
  background-color: #1f2937;
  border-color: #374151;
  color: #f9fafb !important;
}

body.dark-mode .exclusion-symbol-search-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
}

body.dark-mode .exclusion-symbols-container {
  background-color: #111827;
  border-color: #374151;
}

body.dark-mode .exclusion-group-block-title {
  color: #e5e7eb !important;
  border-color: #374151;
}

body.dark-mode .exclusion-group-badge-count {
  background-color: #374151;
  color: #9ca3af;
}

body.dark-mode .exclusion-symbol-item {
  background-color: #1f2937;
  border-color: #374151;
}

body.dark-mode .exclusion-symbol-item:hover {
  background-color: #374151;
  border-color: #4b5563;
}

body.dark-mode .exclusion-symbol-badge {
  background-color: rgba(139, 92, 246, 0.15) !important;
  color: #a78bfa !important;
  border-color: rgba(139, 92, 246, 0.25);
}

body.dark-mode .exclusion-symbol-coin-name {
  color: #d1d5db !important;
}

</style>
